import React from 'react';
import { Box, Stack, Typography, alpha } from '@mui/material';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import DevicesOtherOutlinedIcon from '@mui/icons-material/DevicesOtherOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import { Brand, BrandMark } from '../ui';

const HIGHLIGHTS = [
  { icon: <VpnKeyOutlinedIcon />, title: 'Licence control', body: 'Issue, extend and revoke licences across every product line.' },
  { icon: <DevicesOtherOutlinedIcon />, title: 'Device fleet', body: 'Track bound devices, build props and activation history.' },
  { icon: <ShieldOutlinedIcon />, title: 'Keybox custody', body: 'Rotate attestation keyboxes without shipping a new build.' },
];

interface AuthLayoutProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  /** Sign-in / sign-up cross-link rendered under the card. */
  footer?: React.ReactNode;
}

/**
 * Two-column auth screen: a branded panel that only appears when there is room
 * for it, and the form column which stays centred and full width on mobile.
 */
export const AuthLayout: React.FC<AuthLayoutProps> = ({ title, subtitle, children, footer }) => (
  <Box sx={{ minHeight: '100dvh', display: 'flex', bgcolor: 'background.default' }}>
    <Box
      sx={{
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        justifyContent: 'space-between',
        width: { md: '44%', lg: '46%' },
        maxWidth: 620,
        p: { md: 5, lg: 7 },
        position: 'relative',
        overflow: 'hidden',
        color: 'common.white',
        background: (theme) =>
          `linear-gradient(150deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 55%, ${theme.palette.secondary.dark} 130%)`,
      }}
    >
      {/* Soft radial wash keeps the flat gradient from banding on wide screens. */}
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(60% 50% at 78% 12%, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0) 70%)',
          pointerEvents: 'none',
        }}
      />

      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ position: 'relative' }}>
        <BrandMark size={38} />
        <Box>
          <Typography sx={{ fontSize: '1.0625rem', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2 }}>
            AgentX
          </Typography>
          <Typography
            sx={{
              fontSize: '0.6875rem',
              fontWeight: 600,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              opacity: 0.75,
            }}
          >
            Manager
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ position: 'relative', py: 6 }}>
        <Typography variant="h2" sx={{ mb: 2, maxWidth: '18ch' }}>
          Operations for the whole AgentX estate.
        </Typography>
        <Typography sx={{ opacity: 0.82, maxWidth: '46ch', fontSize: '0.9375rem', lineHeight: 1.65 }}>
          One console for licences, devices, build props and attestation keyboxes — with role-scoped
          access and a full audit trail.
        </Typography>
      </Box>

      <Stack spacing={2} sx={{ position: 'relative' }}>
        {HIGHLIGHTS.map((item) => (
          <Stack key={item.title} direction="row" spacing={2} alignItems="flex-start">
            <Box
              aria-hidden="true"
              sx={{
                display: 'grid',
                placeItems: 'center',
                width: 38,
                height: 38,
                flexShrink: 0,
                borderRadius: 2,
                bgcolor: alpha('#FFFFFF', 0.16),
                border: '1px solid ' + alpha('#FFFFFF', 0.22),
                '& svg': { fontSize: 19 },
              }}
            >
              {item.icon}
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>{item.title}</Typography>
              <Typography sx={{ fontSize: '0.8125rem', opacity: 0.78, lineHeight: 1.55 }}>
                {item.body}
              </Typography>
            </Box>
          </Stack>
        ))}
      </Stack>
    </Box>

    <Box
      sx={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: { xs: 2.5, sm: 4 },
        py: { xs: 5, sm: 6 },
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 420 }}>
        <Box sx={{ display: { xs: 'flex', md: 'none' }, mb: 4 }}>
          <Brand size={38} />
        </Box>

        <Typography variant="h3" component="h1" sx={{ mb: subtitle ? 1 : 3 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3.5 }}>
            {subtitle}
          </Typography>
        )}

        {children}

        {footer && <Box sx={{ mt: 3.5, textAlign: 'center' }}>{footer}</Box>}
      </Box>
    </Box>
  </Box>
);

export default AuthLayout;
